import type { FC } from 'react';
import { useEffect } from 'react';

import { Grid, Box, useRouterTransition } from '@packages/shared';
import { useConfig } from '@packages/utilities';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';

import type { SearchProductCardProps } from '../SearchProductCard';
import { SearchProductCard } from '../SearchProductCard';
import type { CriteoProductCardProps } from '../CriteoProductCard';
import { CriteoProductCard } from '../CriteoProductCard';
import { CriteoBeaconImage } from '../CriteoBeaconImage';
import { ShimmeringBlurOverlay } from '../ShimmeringBlurOverlay';

import { getGridColumns } from './getGridColumns';
import { getCardSize } from './getCardSize';
import { useGlycerinListingsEvents } from './useGlycerinListingsEvents';
import { useCriteoTracking } from './useCriteoTracking';
import { useDisplayMode } from '../../hooks/useDisplayMode';
import { useCardRenderingCounts } from './useCardRenderingCounts';

export const ProductGridFragment = graphql(/* GraphQL */ `
  fragment ProductGridFragment on SearchProductResult {
    sponsoredProducts {
      tracking {
        onLoad
        onView
      }
      items {
        styleId
        primaryVariationGroup {
          sku
        }
        ...CriteoProductCardFragment
      }
    }
    items {
      styleId
      isHighlight
      efficiencyFlags {
        type # actual field is irrelevant, just needed to get the array length
      }
      ratings {
        averageValue # actual field is irrelevant, just needed to get the existence of the parent object
      }
      primaryVariationGroup {
        price {
          unitPackage {
            unit # actual field is irrelevant, just needed to get the existence of the parent object
          }
        }
      }
      ...SearchProductCardFragment
    }
    ...SearchProductCardResultFragment
    ...ProductGridCriteoTrackingFragment
    ...GlycerinListingsEventsResultFragment
    ...UseDisplayModeFragment
  }
`);

export type ProductGridProps = {
  maskedResult: FragmentType<typeof ProductGridFragment>;
  /**
   * Top level categories in the nav tree
   */
  topLevelCategories?: string[];
  /**
   * Current page number for tracking, starting at 1 and incrementing by 1 for each page (for both regular paging and infinite scrolling, where usually there is no page number exposed to the user)
   */
  pageNumberForTracking: number;
  /**
   * Event fired when any card in the grid first becomes visible
   */
  onProductFirstBecameVisible?: (index?: number) => void;
  SponsoredProductCardComponent?: FC<CriteoProductCardProps>;
  StandardProductCardComponent?: FC<SearchProductCardProps>;
};

const ProductGridBlurOverlay: FC<Pick<ProductGridProps, 'topLevelCategories'>> = ({
  topLevelCategories,
}) => {
  const {
    i18n: { languages, defaultLocale },
  } = useConfig();

  const nonDefaultLanguages = languages.filter((language) => !defaultLocale.startsWith(language));
  const productListPrefixes = nonDefaultLanguages
    .map((language) => `/${language}/s/`)
    .concat(['/s/'])
    .concat(topLevelCategories?.map((route) => `/${route}`) ?? []);

  const { isTransitioning } = useRouterTransition((url) =>
    productListPrefixes.some((prefix) => url.startsWith(prefix)),
  );

  return <ShimmeringBlurOverlay show={isTransitioning} />;
};

/**
 * ProductGrid component based on https://mui.com/material-ui/react-grid/
 * */
export const ProductGrid: FC<ProductGridProps> = ({
  maskedResult,
  topLevelCategories,
  pageNumberForTracking,
  onProductFirstBecameVisible,
  SponsoredProductCardComponent = CriteoProductCard,
  StandardProductCardComponent = SearchProductCard,
}) => {
  const result = getFragmentData(ProductGridFragment, maskedResult);

  const { dispatchGlycerinListingsEvent: dispatchProductListTracking } = useGlycerinListingsEvents({
    maskedResult: result,
    pageNumber: pageNumberForTracking,
  });
  const { dispatchCriteoTrackingEvent: dispatchCriteoTracking } = useCriteoTracking({
    maskedResult: result,
  });

  useEffect(() => {
    dispatchProductListTracking();
    dispatchCriteoTracking();
    // The display event should be fired only once, when the grid is initially displayed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { displayMode } = useDisplayMode(result);
  const columns = getGridColumns(displayMode);

  const { sponsoredProductCount, prerenderedProductCount } = useCardRenderingCounts(
    columns,
    !!result.sponsoredProducts,
  );

  const hasAdditionalFlags = result.items.some((item) => item.efficiencyFlags?.length);
  const hasRatings = result.items.some((item) => item.ratings);
  const hasUnitPrices = result.items.some((item) => item.primaryVariationGroup.price.unitPackage);

  return (
    <Box position="relative">
      <ProductGridBlurOverlay topLevelCategories={topLevelCategories} />
      {result.sponsoredProducts && (
        <>
          {result.sponsoredProducts.tracking.onLoad && (
            <CriteoBeaconImage
              src={result.sponsoredProducts.tracking.onLoad}
              name="criteo:onLoad"
            />
          )}
          {result.sponsoredProducts.tracking.onView && (
            <CriteoBeaconImage
              src={result.sponsoredProducts.tracking.onView}
              name="criteo:onView"
            />
          )}
        </>
      )}

      <Grid
        container
        spacing={{ xs: 1.5, md: 2 }}
        columns={columns}
        component="ul"
        sx={{
          listStyle: 'none',
          paddingLeft: 0,
        }}
      >
        {/* always show exactly one full row of criteo products */}
        {result.sponsoredProducts?.items?.slice(0, sponsoredProductCount).map((item) => (
          <Grid
            item
            component="li"
            key={`criteo-${item.styleId}-${item.primaryVariationGroup.sku}`} // criteo cards can have the same styleId, so we need to include the sku in the key
            {...getCardSize(columns, displayMode, false)}
          >
            <SponsoredProductCardComponent
              maskedData={item}
              showAdditionalInfo={displayMode === 'moreInformation'}
            />
          </Grid>
        ))}
        {result.items.map((item, index) => (
          <Grid
            item
            component="li"
            key={item.styleId}
            {...getCardSize(columns, displayMode, (item.isHighlight && index === 0) ?? false)}
          >
            <StandardProductCardComponent
              maskedItem={item}
              maskedResult={result}
              priority={index < prerenderedProductCount}
              alwaysRender={index < prerenderedProductCount}
              onFirstBecameVisible={() => onProductFirstBecameVisible?.(index)}
              useAdditionalFlagsSpacer={hasAdditionalFlags}
              useRatingsSpacer={hasRatings}
              useUnitPriceSpacer={hasUnitPrices}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
